import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { orderAPI, inventoryAPI, productAPI } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Grid,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  Tooltip,
  Badge,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
  FormControlLabel,
  InputAdornment,
  alpha,
  Collapse,
  Pagination,
  Checkbox,
  FormHelperText
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useOrg } from '../../contexts/OrgContext';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  Assignment as AssignmentIcon,
  QrCodeScanner as ScanIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  Upload as UploadIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Description as CertificateIcon,
  PlayArrow as StartIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Science as ScienceIcon,
  Schedule as ScheduleIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApprovedIcon,
  HourglassEmpty as PendingIcon,
  AttachMoney as AttachMoneyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Warehouse as WarehouseIcon
} from '@mui/icons-material';
import { auditService } from '../../services/auditService';
import { inventoryService } from '../../services/inventoryService';

// API Configuration - use REACT_APP_API_URL if available, otherwise default based on NODE_ENV
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const normalizeBatchStatus = (status) => {
  if (!status) return 'PENDING';
  const normalized = status.toUpperCase();
  if (normalized === 'FAILED') return 'FAILED';
  if (normalized === 'PASSED' || normalized === 'COMPLETED') return 'COMPLETED';
  if (normalized === 'IN_PROGRESS' || normalized === 'RECEIVING' || normalized === 'PROCESSING') return 'IN_PROGRESS';
  return 'PENDING';
};

// No mock data: all data is fetched from backend APIs

const ReceiveInventoryWorkflow = ({ currentCompany }) => {
  const { org } = useOrg();
  const effectiveOrg = (currentCompany || org || '').toLowerCase();
  const orgKey = effectiveOrg;
  const orgUpper = effectiveOrg.toUpperCase();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const poId = searchParams.get('po');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get current user for Super Admin check
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    const userStr = localStorage.getItem('currentUser') || sessionStorage.getItem('barebiologics_currentUser');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
  }, []);
  const isSuperAdmin = currentUser?.isSuperAdmin === true;

  // State - Following Order Processing pattern
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workflowError, setWorkflowError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Pending Receipt, 1: In Progress, 2: Completed, 3: Product Management, 4: Inventory Designation

  // EPL Batch Management State
  const [createBatchOpen, setCreateBatchOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchViewMode, setBatchViewMode] = useState('details'); // 'details' or 'sku'
  const [successMessage, setSuccessMessage] = useState('');
  const [batchErrorMessage, setBatchErrorMessage] = useState('');
  
  // Batch Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterExpiration, setFilterExpiration] = useState('');
  const [filterLot, setFilterLot] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');

  // Batch Form State
  const [batchForm, setBatchForm] = useState({
    productId: '',
    lotNumber: '',
    quantity: '',
    startingSku: '',
    expirationDate: '',
    manufacturingDate: new Date().toISOString().split('T')[0],
    costPerUnit: '',
    donorId: '' // Optional - link batch to donor
  });
  
  // Donor options for batch creation
  const [donorOptions, setDonorOptions] = useState([]);
  const [donorsLoading, setDonorsLoading] = useState(false);

  // Edit and Delete State for Super Admins
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  
  
  const [editForm, setEditForm] = useState({
    lotNumber: '',
    expirationDate: '',
    quantityReceived: '',
    quantityAvailable: '',
    warehouse: '',
    zone: '',
    shelf: '',
    bin: '',
    qcStatus: 'PENDING',
    qcNotes: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [productOptions, setProductOptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState('');
  const [productSuccessMessage, setProductSuccessMessage] = useState('');
  const [productForm, setProductForm] = useState({
    id: null,
    name: '',
    sku: '',
    description: '',
    volume: '',
    basePrice: '',
    imageFile: null,
    imagePreview: '',
    images: [],
    organization: orgUpper || 'EPL'
  });
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  // Batch pagination & selection state
  const itemsPerPage = 50;
  const [batchSkuPages, setBatchSkuPages] = useState({});
  const [expandedBatchId, setExpandedBatchId] = useState(null);
  const [batchSkusMap, setBatchSkusMap] = useState({});
  const [batchSkusLoading, setBatchSkusLoading] = useState({});
  const [exportingBatches, setExportingBatches] = useState(false);


  // Detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedBatchDetails, setSelectedBatchDetails] = useState(null);
  const [selectedSkuDetails, setSelectedSkuDetails] = useState(null);

  // Receiving workflow state
  const [receivingItems, setReceivingItems] = useState([]);
  const [scannedSKU, setScannedSKU] = useState('');
  const [scanMessage, setScanMessage] = useState('');
  const [scanSeverity, setScanSeverity] = useState('info');
  
  // Quality certificate upload state (REQUIRED)
  const [qualityCertificate, setQualityCertificate] = useState(null);
  const [certificateError, setCertificateError] = useState(null);
  const [certificateUploading, setCertificateUploading] = useState(false);
  
  // Receiving details
  // Tracking number removed per business rule (not captured during receiving)

  const scanInputRef = useRef(null);

  // Load purchase orders
  useEffect(() => {
    fetchPurchaseOrders();
  }, [currentCompany]);

  // Auto-focus scan input when in scanning step
  useEffect(() => {
    if (currentStep === 1 && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [currentStep]);

  // Auto-open if PO ID in URL
  useEffect(() => {
    if (poId && purchaseOrders.length > 0) {
      const po = purchaseOrders.find(p => p.id === poId);
      if (po) {
        handleStartReceiving(po);
      }
    }
  }, [poId, purchaseOrders]);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      console.log('📦 Fetching purchase orders for org:', currentCompany);
      
      const response = await orderAPI.getOrders({ organization: currentCompany });
      setPurchaseOrders(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
      setError('Failed to load purchase orders');
      setPurchaseOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStartReceiving = (poOrBatch) => {
    if (poOrBatch?.batchId) {
      navigate(`/internal/${orgKey}/receive-inventory/batches/${poOrBatch.id}`);
      return;
    }

    const isEPLBatch = orgKey === 'epl' && !!poOrBatch?.batchId;
    
    // Check for existing workflow state
    const existingState = auditService.loadWorkflowState(poOrBatch.id);
    
    if (existingState) {
      // Resume from saved state
      setSelectedPO(existingState.selectedPO);
      setReceivingItems(existingState.receivingItems);
      setCurrentStep(existingState.currentStep);
      setQualityCertificate(existingState.qualityCertificate);
      // tracking number removed
      
      // Log resumption
      auditService.logAction({
        type: isEPLBatch ? 'BATCH_RECEIVING_RESUMED' : 'RECEIVING_RESUMED',
        entityType: isEPLBatch ? 'batch' : 'purchase_order',
        entityId: poOrBatch.id,
        userName: 'Demo User',
        details: `${isEPLBatch ? 'Batch' : 'Purchase order'} receiving workflow resumed from step ${existingState.currentStep}`,
        previousState: existingState
      });
    } else {
      // Start new workflow
      const updated = {
        ...poOrBatch,
        assignedTo: 'Demo User',
        assignedAt: new Date().toISOString(),
        status: 'RECEIVING'
      };

      setSelectedPO(updated);
      setReceivingItems(poOrBatch.items.map(item => ({
        ...item,
        receivedQuantity: 0,
        qcStatus: 'PENDING',
        lotNumber: isEPLBatch ? poOrBatch.lotNumber : '',
        expirationDate: isEPLBatch ? poOrBatch.expirationDate : '',
        notes: '',
        inventoryRecords: []
      })));
      setCurrentStep(0);
      setQualityCertificate(null);
      setCertificateError(null);
      // tracking number removed

      // Log workflow start
      auditService.logAction({
        type: isEPLBatch ? 'BATCH_RECEIVING_STARTED' : 'RECEIVING_STARTED',
        entityType: isEPLBatch ? 'batch' : 'purchase_order',
        entityId: poOrBatch.id,
        userName: 'Demo User',
        userRole: 'Warehouse Manager',
        location: 'Main Warehouse',
        details: `${isEPLBatch ? 'Batch' : 'Purchase order'} receiving workflow started`,
        newState: isEPLBatch 
          ? { batchId: poOrBatch.batchId, lotNumber: poOrBatch.lotNumber, expectedQuantity: poOrBatch.expectedQuantity }
          : { poNumber: poOrBatch.poNumber, vendor: poOrBatch.vendor, totalAmount: poOrBatch.totalAmount }
      });

      // Update batch status in batches list if EPL
      if (isEPLBatch) {
        setBatches(batches.map(b => b.id === poOrBatch.id ? updated : b));
      }
    }
    
    setProcessDialogOpen(true);
  };

  const handleAddInventoryRecord = () => {
    // Add a new inventory record for the current PO item being processed
    const currentPOItem = selectedPO?.items?.find(item => {
      const receivingItem = receivingItems.find(ri => ri.productId === item.productId);
      return !receivingItem || receivingItem.receivedQuantity < item.quantity;
    });

    if (currentPOItem) {
      const newSKU = scannedSKU.trim().toUpperCase() || `${currentPOItem.productId}-${Date.now().toString().slice(-6)}`;
      
      // Store cost price for later evaluation
      const costPrice = currentPOItem.unitCost;
      const sellPrice = 0; // Will be set when product pricing is determined
      
      // Create new inventory record with full audit trail
      const productName = typeof currentPOItem.product === 'object' && currentPOItem.product !== null
        ? currentPOItem.product.name || currentPOItem.product.sku || 'Unknown Product'
        : currentPOItem.product || 'Unknown Product';
      
      const newInventoryRecord = {
        id: Date.now().toString(),
        productId: currentPOItem.productId,
        productName: productName,
        sku: newSKU,
        lotNumber: newSKU, // Use SKU as lot number for now
        expirationDate: '',
        quantityReceived: 1,
        receivedDate: new Date().toISOString(),
        receivedBy: 'Demo User', // Replace with actual user
        qcStatus: 'PENDING',
        notes: '',
        // Cost tracking for internal records only
        costPrice: costPrice,
        // FDA compliance fields
        auditTrail: {
          receivedBy: 'Demo User',
          receivedAt: new Date().toISOString(),
          location: 'Main Warehouse',
          qcUploadedBy: null,
          qcUploadedAt: null,
          qcApprovedBy: null,
          qcApprovedAt: null,
          soldTo: null,
          soldAt: null,
          packedBy: null,
          packedAt: null,
          shippedBy: null,
          shippedAt: null,
          trackingNumber: null
        }
      };

      // Update receiving items
      const updatedItems = receivingItems.map(item => {
        if (item.productId === currentPOItem.productId) {
          return { ...item, receivedQuantity: item.receivedQuantity + 1 };
        }
        return item;
      });

      // If this product doesn't exist in receiving items yet, add it
      if (!receivingItems.find(item => item.productId === currentPOItem.productId)) {
        updatedItems.push({
          ...currentPOItem,
          receivedQuantity: 1,
          inventoryRecords: [newInventoryRecord]
        });
      } else {
        // Add inventory record to existing item
        const itemIndex = updatedItems.findIndex(item => item.productId === currentPOItem.productId);
        if (!updatedItems[itemIndex].inventoryRecords) {
          updatedItems[itemIndex].inventoryRecords = [];
        }
        updatedItems[itemIndex].inventoryRecords.push(newInventoryRecord);
      }

      setReceivingItems(updatedItems);

      // Log inventory creation for FDA compliance
      auditService.logAction({
        type: 'INVENTORY_RECEIVED',
        entityType: 'inventory_batch',
        entityId: newInventoryRecord.id,
        userName: 'Demo User',
        userRole: 'Warehouse Manager',
        location: 'Main Warehouse',
        details: `New inventory record created: ${newSKU} for ${productName}`,
        newState: {
          sku: newSKU,
          product: productName,
          quantity: 1,
          costPrice: costPrice
        }
      });

      // Save workflow state for persistence
      if (selectedPO?.id) {
        auditService.saveWorkflowState(selectedPO.id, {
        selectedPO,
        receivingItems: updatedItems,
        currentStep,
        qualityCertificate
      });
      }

      setScanMessage(`✓ ${newSKU} created`);
      setScanSeverity('success');
      setScannedSKU('');

      // Auto-advance if all items received
      const totalReceived = updatedItems.reduce((sum, item) => sum + item.receivedQuantity, 0);
      const totalExpected = selectedPO?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      
      if (totalReceived >= totalExpected) {
        setTimeout(() => {
          setCurrentStep(2);
          setScanMessage('');
        }, 1500);
      }
    }
  };

  const handleSKUScan = (e) => {
    if (e.key === 'Enter') {
      handleAddInventoryRecord();
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setCertificateError('Please upload a PDF, JPEG, or PNG file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setCertificateError('File size must be less than 10MB');
      return;
    }

    try {
      setCertificateUploading(true);
      setCertificateError(null);
      
      // TODO: Upload to Azure Blob Storage or file system
      // For now, just store file reference
      const certificate = {
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        uploadedBy: 'Demo User' // Replace with actual user
      };
      
      setQualityCertificate(certificate);

      // Log quality certificate upload for FDA compliance
      auditService.logAction({
        type: 'QC_CERTIFICATE_UPLOADED',
        entityType: 'purchase_order',
        entityId: selectedPO?.id,
        userName: 'Demo User',
        userRole: 'Quality Manager',
        location: 'Quality Lab',
        details: `Quality certificate uploaded: ${file.name} (${(file.size / 1024).toFixed(1)}KB)`,
        newState: {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type
        }
      });

      // Save workflow state
      if (selectedPO?.id) {
        auditService.saveWorkflowState(selectedPO.id, {
        selectedPO,
        receivingItems,
        currentStep,
        qualityCertificate: certificate
      });
      }

      console.log('Quality certificate uploaded with audit trail:', file.name);
    } catch (err) {
      console.error('Certificate upload error:', err);
      setCertificateError('Failed to upload certificate');
    } finally {
      setCertificateUploading(false);
    }
  };

  // View quality certificate for completed batches
  const handleViewCertificate = (batch) => {
    if (batch.qualityCertificate) {
      // Open certificate in new tab
      window.open(batch.qualityCertificate.url || batch.qualityCertificate.dataUrl, '_blank');
    } else {
      setError('No quality certificate available for this batch');
    }
  };

  // Super Admin: Handle Edit
  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setEditForm({
      lotNumber: record.lotNumber || '',
      expirationDate: record.expirationDate ? new Date(record.expirationDate).toISOString().split('T')[0] : '',
      quantityReceived: record.quantityReceived || record.expectedQuantity || '',
      quantityAvailable: record.quantityAvailable || record.quantityReceived || '',
      warehouse: record.warehouse || '',
      zone: record.zone || '',
      shelf: record.shelf || '',
      bin: record.bin || '',
      qcStatus: record.qcStatus || 'PENDING',
      qcNotes: record.qcNotes || ''
    });
    setEditDialogOpen(true);
  };

  // Super Admin: Handle Save Edit
  const handleSaveEdit = async () => {
    if (!selectedRecord) return;

    try {
      setEditLoading(true);
      const baseUrl = API_BASE_URL;
      const token = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'X-Client-Version': '2.0.0',
        'X-HIPAA-Compliant': 'true'
      };

      const updateData = {
        quantityAvailable: parseInt(editForm.quantityAvailable, 10),
        warehouse: editForm.warehouse || null,
        zone: editForm.zone || null,
        shelf: editForm.shelf || null,
        bin: editForm.bin || null,
        qcStatus: editForm.qcStatus,
        qcNotes: editForm.qcNotes || null
      };

      // Update inventory batch
      const response = await fetch(`${baseUrl}/inventory/${selectedRecord.id}`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update inventory: ${response.status} ${errorText}`);
      }

      const result = await response.json();

      // Update batch record (for EPL batches)
      if (orgKey === 'epl') {
        const updatedBatch = {
          ...selectedRecord,
          quantityAvailable: parseInt(editForm.quantityAvailable, 10),
          warehouse: editForm.warehouse,
          zone: editForm.zone,
          shelf: editForm.shelf,
          bin: editForm.bin,
          qcStatus: editForm.qcStatus,
          qcNotes: editForm.qcNotes
        };
        setBatches(batches.map(b => b.id === selectedRecord.id ? updatedBatch : b));
      } else {
        // Update purchase order
        const updatedPO = {
          ...selectedRecord,
          warehouse: editForm.warehouse,
          zone: editForm.zone,
          shelf: editForm.shelf,
          bin: editForm.bin,
          qcStatus: editForm.qcStatus,
          qcNotes: editForm.qcNotes
        };
        setPurchaseOrders(purchaseOrders.map(po => po.id === selectedRecord.id ? updatedPO : po));
      }

      // Audit tracking
      auditService.logAction({
        type: 'INVENTORY_RECORD_EDITED',
        entityType: orgKey === 'epl' ? 'batch' : 'purchase_order',
        entityId: selectedRecord.id,
        userName: currentUser?.name || currentUser?.email || 'Super Admin',
        userRole: 'Super Admin',
        location: 'System',
        details: `Super Admin edited inventory record: ${selectedRecord.batchId || selectedRecord.poNumber}`,
        previousState: selectedRecord,
        newState: {
          ...selectedRecord,
          ...updateData
        }
      });

      setEditDialogOpen(false);
      setSuccessMessage('Inventory record updated successfully');
      fetchBatches(); // Refresh batches
    } catch (error) {
      console.error('Error updating inventory:', error);
      setError('Failed to update inventory record: ' + error.message);
    } finally {
      setEditLoading(false);
    }
  };

  // Super Admin: Handle Delete
  const handleDeleteRecord = (record) => {
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  // Super Admin: Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedRecord) return;

    try {
      setDeleteLoading(true);
      
      // For EPL batches, check if batch is in active stage (only PENDING batches can be deleted)
      if (orgKey === 'epl' && selectedRecord.qcStatus && selectedRecord.qcStatus !== 'PENDING' && selectedRecord.qcStatus !== null) {
        setError('Cannot delete batch that has been completed. Only batches in PENDING status can be deleted.');
        setDeleteDialogOpen(false);
        return;
      }

      // Use batch deletion endpoint for EPL batches
      if (orgKey === 'epl') {
        const response = await inventoryAPI.deleteBatch(selectedRecord.id);
        if (response.success) {
          // Audit tracking before removing from state
          auditService.logAction({
            type: 'INVENTORY_BATCH_DELETED',
            entityType: 'batch',
            entityId: selectedRecord.id,
            userName: currentUser?.name || currentUser?.email || 'User',
            userRole: 'Internal User',
            location: 'System',
            details: `Deleted batch: ${selectedRecord.batchId}. Deleted ${response.data?.deletedSkus || 0} SKUs and ${response.data?.deletedMovements || 0} movements.`,
            previousState: selectedRecord,
            newState: null
          });

          // Remove from state
          setBatches(batches.filter(b => b.id !== selectedRecord.id));
          setDeleteDialogOpen(false);
          setSuccessMessage(`Batch ${selectedRecord.batchId} deleted successfully. ${response.data?.deletedSkus || 0} SKUs and ${response.data?.deletedMovements || 0} movements removed.`);
          fetchBatches(); // Refresh batches
        } else {
          throw new Error(response.message || 'Failed to delete batch');
        }
      } else {
        // For BARE purchase orders, use the existing endpoint
        const baseUrl = API_BASE_URL;
        const token = localStorage.getItem('authToken');
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'X-Client-Version': '2.0.0',
          'X-HIPAA-Compliant': 'true'
        };

        const response = await fetch(`${baseUrl}/inventory/${selectedRecord.id}`, {
          method: 'DELETE',
          headers,
          credentials: 'include'
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to delete inventory: ${response.status} ${errorText}`);
        }

        // Audit tracking before removing from state
        auditService.logAction({
          type: 'INVENTORY_RECORD_DELETED',
          entityType: 'purchase_order',
          entityId: selectedRecord.id,
          userName: currentUser?.name || currentUser?.email || 'Super Admin',
          userRole: 'Super Admin',
          location: 'System',
          details: `Super Admin deleted inventory record: ${selectedRecord.poNumber}`,
          previousState: selectedRecord,
          newState: null
        });

        // Remove from state
        setPurchaseOrders(purchaseOrders.filter(po => po.id !== selectedRecord.id));
        setDeleteDialogOpen(false);
        setSuccessMessage('Inventory record deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting inventory:', error);
      setError('Failed to delete inventory record: ' + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCompleteReceiving = async () => {
    try {
      const isEPLBatch = orgKey === 'epl' && selectedPO?.batchId;
      
      // Validate quality certificate is uploaded
      if (!qualityCertificate) {
        setCertificateError('Quality certificate upload is required');
        return;
      }

      const completionTimestamp = new Date().toISOString();
      const completedBy = 'Demo User'; // Replace with actual user

      // Update batch status in database
      if (isEPLBatch) {
        try {
          const baseUrl = API_BASE_URL;
          
          const token = localStorage.getItem('authToken');
          const headers = {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
            'X-Client-Version': '2.0.0',
            'X-HIPAA-Compliant': 'true'
          };
          
          console.log('🔄 Updating batch status in database...', {
            batchId: selectedPO.id,
            qcStatus: 'COMPLETED',
            qcNotes: `Quality certificate uploaded: ${qualityCertificate.name}`,
            qcPerformedBy: completedBy
          });
          
          const response = await fetch(`${baseUrl}/inventory/batches/${selectedPO.id}`, {
            method: 'PATCH',
            headers,
            credentials: 'include',
            body: JSON.stringify({
              qcStatus: 'COMPLETED',
              qcNotes: `Quality certificate uploaded: ${qualityCertificate.name}`,
              qcPerformedBy: completedBy
            })
          });
          
          console.log('📡 API Response:', {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ API Error:', errorText);
            throw new Error(`Failed to update batch status in database: ${response.status} ${response.statusText}`);
          }
          
          const result = await response.json();
          console.log('✅ Batch status updated in database:', result);
        } catch (error) {
          console.error('❌ Error updating batch status:', error);
          // Continue with local update even if API fails
        }
      }

      // Update all inventory records with QC approval
      const updatedReceivingItems = receivingItems.map(item => ({
        ...item,
        inventoryRecords: item.inventoryRecords?.map(record => ({
          ...record,
          qcStatus: 'APPROVED',
          qcApprovedBy: completedBy,
          qcApprovedAt: completionTimestamp,
          availableForSale: true,
          auditTrail: {
            ...record.auditTrail,
            qcUploadedBy: qualityCertificate.uploadedBy,
            qcUploadedAt: qualityCertificate.uploadedAt,
            qcApprovedBy: completedBy,
            qcApprovedAt: completionTimestamp
          }
        })) || []
      }));

      const completed = {
        ...selectedPO,
        status: isEPLBatch ? 'COMPLETED' : 'RECEIVED',
        receivedAt: completionTimestamp,
        receivedBy: completedBy,
        receivingItems: updatedReceivingItems,
        qualityCertificate,
        // tracking number removed
      };

      // Update scannedQuantity and quantityReceived for EPL batches
      if (isEPLBatch) {
        completed.scannedQuantity = updatedReceivingItems.reduce((sum, item) => sum + item.receivedQuantity, 0);
        completed.quantityReceived = completed.scannedQuantity;
        completed.quantityAvailable = completed.scannedQuantity;
      }

      // Comprehensive FDA compliance logging
      auditService.logAction({
        type: isEPLBatch ? 'BATCH_COMPLETED' : 'RECEIVING_COMPLETED',
        entityType: isEPLBatch ? 'batch' : 'purchase_order',
        entityId: selectedPO?.id,
        userName: completedBy,
        userRole: 'Warehouse Manager',
        location: 'Main Warehouse',
        details: `${isEPLBatch ? 'Batch' : 'Purchase order'} receiving completed. ${updatedReceivingItems.reduce((sum, item) => sum + item.receivedQuantity, 0)} items added to inventory`,
        previousState: selectedPO,
        newState: completed
      });

      // Log each inventory record as available for sale
      const inventoryBatch = [];
      updatedReceivingItems.forEach(item => {
        item.inventoryRecords?.forEach(record => {
          auditService.logAction({
            type: 'INVENTORY_APPROVED_FOR_SALE',
            entityType: 'inventory_batch',
            entityId: record.id,
            userName: completedBy,
            userRole: 'Quality Manager',
            location: 'Quality Lab',
            details: `Inventory batch ${record.sku} approved for sale after QC review`,
            newState: {
              sku: record.sku,
              product: record.productName,
              costPrice: record.costPrice,
              availableForSale: true
            }
          });

          // Add to inventory batch for the inventory system
          inventoryBatch.push({
            ...record,
            organization: selectedPO?.organization,
            availableForSale: true
          });
        });
      });

      // Add completed inventory to available inventory system
      if (inventoryBatch.length > 0) {
        inventoryService.addReceivedInventory(inventoryBatch);
      }

      // Clear workflow state (process completed)
      if (selectedPO?.id) {
        auditService.clearWorkflowState(selectedPO.id);
      }
      
      // Update list (PO or Batch)
      if (isEPLBatch) {
        setBatches(batches.map(b => b.id === selectedPO?.id ? completed : b));
        // Refresh batches from database to get updated status
        fetchBatches();
      } else {
        const updatedPOs = purchaseOrders.map(po =>
          po.id === selectedPO?.id ? completed : po
        );
        setPurchaseOrders(updatedPOs);
      }
      
      setProcessDialogOpen(false);
      setCurrentStep(0);
      setSuccessMessage(`${isEPLBatch ? 'Batch' : 'Purchase order'} received successfully! ${updatedReceivingItems.reduce((sum, item) => sum + item.receivedQuantity, 0)} items now available for sale.`);

      console.log(`FDA Compliance: ${isEPLBatch ? 'Batch' : 'PO'} receiving completed with full audit trail`);

    } catch (err) {
      console.error('Error completing receiving:', err);
      setWorkflowError('Failed to complete receiving process');
      
      // Log error for audit trail
      auditService.logAction({
        type: 'RECEIVING_ERROR',
        entityType: orgKey === 'epl' ? 'batch' : 'purchase_order',
        entityId: selectedPO?.id,
        userName: 'Demo User',
        details: `Error completing receiving: ${err.message}`,
        newState: { error: err.message }
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
      case 'ORDERED': return 'warning';
      case 'RECEIVING': return 'info';
      case 'RECEIVED': return 'success';
      default: return 'default';
    }
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  // Filter POs/Batches by tab
  const filteredPurchaseOrders = purchaseOrders.filter(po => {
    if (orgKey === 'epl') return false;
    switch (activeTab) {
      case 0: return ['PENDING', 'ORDERED'].includes(po.status);
      case 1: return po.status === 'RECEIVING';
      case 2: return po.status === 'RECEIVED';
      default: return true;
    }
  });

  // Render mobile view
  const renderMobilePOs = () => (
    <Stack spacing={2}>
      {filteredPurchaseOrders.map((po) => (
        <Paper key={po.id} variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
              {po.poNumber}
            </Typography>
            <Chip
              label={po.status}
              color={getStatusColor(po.status)}
              size="small"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {po.vendor}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ${(Number(po.totalAmount) || 0).toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Expected: {new Date(po.expectedDelivery).toLocaleDateString()}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {activeTab === 0 && (
              <Button
                size="small"
                variant="contained"
                startIcon={<StartIcon />}
                onClick={() => handleStartReceiving(po)}
                sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
              >
                {po.status === 'ORDERED' ? 'Start Receiving' : 'Continue'}
              </Button>
            )}
            {activeTab === 1 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ViewIcon />}
                onClick={() => handleStartReceiving(po)}
                sx={{ borderColor: '#3b82f6', color: '#3b82f6' }}
              >
                Continue
              </Button>
            )}
            <Typography variant="body2" sx={{ ml: 'auto' }}>
              {po.assignedTo || 'Unassigned'}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );

  // EPL Products
  // Products will be loaded from API when creating batch (dropdown uses existing list fetched elsewhere if present)

  // Fetch batches for EPL
  const fetchBatches = async () => {
    if (orgKey === 'epl') {
      try {
        console.log('📦 Fetching batches for EPL organization');
        
        const response = await inventoryAPI.getInventoryLevels({
          organization: currentCompany ? currentCompany.toUpperCase() : orgUpper?.toUpperCase()
        });
        
        // Transform API response to match expected format
        const transformedBatches = (response.data || []).map(batch => {
          const normalizedStatus = normalizeBatchStatus(batch.qcStatus || batch.status);
          return {
          id: batch.id,
          batchId: batch.batchId || batch.id,
          productId: batch.productId,
          productName: batch.product?.name || 'Unknown Product',
          productSku: batch.product?.sku || batch.product?.code || batch.product?.skuCode || '',
          lotNumber: batch.lotNumber,
          expectedQuantity: batch.quantityReceived || batch.quantityAvailable || 0,
          scannedQuantity: batch.quantityReceived || 0,
          quantityReceived: batch.quantityReceived || 0,
          quantityAvailable: batch.quantityAvailable || 0,
          quantityReserved: batch.quantityReserved || 0,
          quantityAllocated: batch.quantityAllocated || 0,
          expirationDate: batch.expirationDate,
          manufacturingDate: batch.manufacturingDate,
          status: normalizedStatus,
          qcStatus: normalizedStatus,
          warehouse: batch.warehouse,
          zone: batch.zone,
          shelf: batch.shelf,
          bin: batch.bin,
          createdAt: batch.createdAt,
          receivedAt: batch.receivedAt,
          items: batch.items || [],
          organization: batch.organization || 'EPL'
        };
        });
        
        setBatches(transformedBatches);
        setBatchSkusMap((prev) => {
          const next = {};
          transformedBatches.forEach((batch) => {
            if (prev[batch.id]) {
              next[batch.id] = prev[batch.id];
            }
          });
          return next;
        });
        setBatchSkuPages((prev) => {
          const next = {};
          transformedBatches.forEach((batch) => {
            if (prev[batch.id]) {
              next[batch.id] = prev[batch.id];
            }
          });
          return next;
        });
      } catch (error) {
        console.error('Error fetching batches:', error);
        setBatches([]);
      }
    }
  };

  useEffect(() => {
    if (orgKey === 'epl') {
      fetchBatches();
    }
  }, [org, currentCompany]);


  // Handle batch creation
  const handleCreateBatch = async () => {
    setBatchErrorMessage('');
    setSuccessMessage('');

    try {
      if (!batchForm.productId || !batchForm.donorId || !batchForm.quantity || !batchForm.expirationDate || !batchForm.costPerUnit || !batchForm.startingSku) {
        setBatchErrorMessage('Please fill in all required fields');
        return;
      }

      // Call backend to create batch + generate SKUs server-side
      const baseUrl = API_BASE_URL;
      
      // Get auth token for API calls - use same pattern as main API service
      const token = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'X-Client-Version': '2.0.0',
        'X-HIPAA-Compliant': 'true'
      };
      
      const response = await fetch(`${baseUrl}/inventory/batches`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          productId: batchForm.productId,
          organization: orgUpper || (currentCompany ? currentCompany.toUpperCase() : 'EPL'),
          lotNumber: batchForm.donorId ? donorOptions.find(d => d.id === batchForm.donorId)?.donorId || batchForm.lotNumber : batchForm.lotNumber,
          expirationDate: batchForm.expirationDate,
          manufacturingDate: batchForm.manufacturingDate,
          quantity: parseInt(batchForm.quantity, 10),
          costPerUnit: parseFloat(batchForm.costPerUnit),
          startingSku: batchForm.startingSku,
          donorId: batchForm.donorId || null,
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to create batch');
      }

      const result = await response.json();
      const skuRange = result.actualStartSku && result.actualEndSku 
        ? ` (${result.actualStartSku} - ${result.actualEndSku})`
        : '';
      setSuccessMessage(`Batch created successfully. Generated ${result.createdSkus} SKUs${skuRange}.`);
      setCreateBatchOpen(false);
      resetBatchForm();
      setActiveTab(0);
      fetchBatches(); // Refresh the batches list to show the new batch
    } catch (error) {
      console.error('Error creating batch:', error);
      setBatchErrorMessage('Failed to create batch. Please try again.');
    }
  };


  const resetBatchForm = () => {
    setBatchForm({
      productId: '',
      lotNumber: '',
      quantity: '',
      expirationDate: '',
      manufacturingDate: new Date().toISOString().split('T')[0],
      costPerUnit: '',
      startingSku: '', // Add default value for startingSku to fix controlled input warning
      donorId: ''
    });
  };
  
  // Fetch donors for dropdown (EPL only)
  const fetchDonors = async () => {
    if (orgUpper !== 'EPL') return;
    
    try {
      setDonorsLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDonorOptions(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching donors:', err);
    } finally {
      setDonorsLoading(false);
    }
  };

  useEffect(() => {
    if (!batchForm.productId && productOptions.length > 0) {
      setBatchForm((prev) => ({ ...prev, productId: productOptions[0].id }));
    }
  }, [productOptions]);

  const fetchProductsForOrg = async (options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setProductLoading(true);
      setProductError('');
    }

    try {
      const orgFilter = ['BARE', 'EPL', 'BOTH'].includes(orgUpper) ? orgUpper : undefined;
      const limit = 100; // backend max
      const response = await productAPI.getProducts({
        includeInactive: true,
        limit,
        ...(orgFilter ? { organization: orgFilter } : {})
      });
      if (!response?.success) {
        throw new Error(response?.error || response?.message || 'Failed to load products');
      }

      const json = response;
      const filtered = (json?.data || []).filter((p) => {
        const productOrg = (p.organization || '').toUpperCase();
        if (!orgFilter) return true;
        if (orgFilter === 'EPL') return productOrg === 'EPL' || productOrg === 'BOTH';
        if (orgFilter === 'BARE') return productOrg === 'BARE' || productOrg === 'BOTH';
        if (orgFilter === 'BOTH') return productOrg === 'BOTH';
        return true;
      });

      setProducts(filtered);
      setProductOptions(filtered);
    } catch (error) {
      console.error('❌ Error loading products:', error);
      setProducts([]);
      setProductOptions([]);
      if (!silent) {
        setProductError('Failed to load products. Please try again.');
      }
    } finally {
      if (!silent) {
        setProductLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchProductsForOrg();
  }, [org, currentCompany]);

  useEffect(() => {
    if (createBatchOpen) {
      fetchProductsForOrg({ silent: true });
      if (orgUpper === 'EPL') {
        fetchDonors();
      }
    }
  }, [createBatchOpen, org, currentCompany]);

  const resetProductFormState = () => {
    setProductForm(prev => {
      if (prev.imagePreview) URL.revokeObjectURL(prev.imagePreview);
      return {
        id: null,
        name: '',
        sku: '',
        description: '',
        volume: '',
        basePrice: '',
        imageFile: null,
        imagePreview: '',
        images: [],
        organization: orgUpper === 'EPL' ? 'EPL' : orgUpper === 'BARE' ? 'BARE' : 'BOTH'
      };
    });
    setProductError('');
  };

  const openProductDialog = (product) => {
    if (product) {
      const existingImages = Array.isArray(product.images) ? product.images : [];
      setProductForm({
        id: product.id,
        name: product.name || '',
        sku: product.sku || '',
        description: product.description || '',
        volume: product.volume || '',
        basePrice: product.basePrice ? String(product.basePrice) : '',
        imageFile: null, // Always start with no new file
        imagePreview: existingImages.length > 0 ? existingImages[0] : null, // Use null instead of empty string
        images: existingImages, // Store original images to detect removal
        organization: product.organization || (orgUpper === 'EPL' ? 'EPL' : 'BARE')
      });
    } else {
      resetProductFormState();
    }
    setProductDialogOpen(true);
  };

  const handleProductFieldChange = (field) => (event) => {
    const value = event.target.value;
    setProductForm(prev => ({ ...prev, [field]: value }));
  };

  const handleProductImageChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    
    // Validate file size (20MB limit to match backend)
    const maxSizeBytes = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSizeBytes) {
      setProductError(`Image file is too large. Maximum size is 20MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB. Please compress the image or use a smaller file.`);
      event.target.value = ''; // Clear the file input
      return;
    }
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setProductError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
      event.target.value = ''; // Clear the file input
      return;
    }
    
    setProductError(''); // Clear any previous errors
    setProductForm(prev => {
      if (prev.imagePreview && prev.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(prev.imagePreview);
      }
      return {
        ...prev,
        imageFile: file,
        imagePreview: URL.createObjectURL(file)
      };
    });
  };

  const handleRemoveProductImage = () => {
    setProductForm(prev => {
      // Revoke blob URL if it exists
      if (prev.imagePreview && prev.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(prev.imagePreview);
      }
      // Clear all image-related state
      return { 
        ...prev, 
        imageFile: null, 
        imagePreview: null, // Set to null instead of empty string to indicate removal
        images: [] 
      };
    });
  };

  const handleProductSave = async (event) => {
    event.preventDefault();
    setProductError('');
    setProductSuccessMessage('');

    if (!productForm.name.trim() || !productForm.sku.trim()) {
      setProductError('Product name and SKU are required');
      return;
    }

    const parsedPrice = parseFloat(productForm.basePrice || '0');
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      setProductError('Sale price must be a valid non-negative number');
      return;
    }

    try {
      setProductLoading(true);
      const payload = {
        sku: productForm.sku.trim(),
        name: productForm.name.trim(),
        description: productForm.description.trim() || undefined,
        volume: productForm.volume.trim() || undefined,
        basePrice: parsedPrice,
        organization: productForm.organization || (orgUpper === 'EPL' ? 'EPL' : 'BARE'),
        category: orgUpper === 'EPL' ? 'EQUINE' : 'HUMAN'
      };

      let response;
      if (productForm.id) {
        response = await productAPI.updateProduct(productForm.id, payload);
      } else {
        response = await productAPI.createProduct(payload);
      }

      const savedProduct = response?.data;
      
      // Handle image operations
      if (savedProduct?.id) {
        // Check if image was removed: had images before but now imagePreview is null/empty and no new file
        const hadImagesBefore = productForm.id && productForm.images && productForm.images.length > 0;
        const hasImageNow = productForm.imagePreview && productForm.imagePreview !== '';
        const imageRemoved = hadImagesBefore && !productForm.imageFile && !hasImageNow;
        
        if (imageRemoved) {
          // Product was edited and image was removed - clear images from backend
          try {
            await productAPI.deleteProductImage(savedProduct.id);
            savedProduct.images = []; // Update local state
          } catch (error) {
            console.warn('Failed to delete product image:', error);
            // Continue even if image deletion fails
          }
        } else if (productForm.imageFile) {
          // Upload new image (replaces existing images on backend)
          const uploadResponse = await productAPI.uploadProductImage(savedProduct.id, productForm.imageFile);
          savedProduct.images = uploadResponse?.data?.images || savedProduct.images;
        }
      }

      setProductSuccessMessage(`Product ${productForm.id ? 'updated' : 'created'} successfully.`);
      setProductDialogOpen(false);
      resetProductFormState();
      await fetchProductsForOrg({ silent: true });
    } catch (error) {
      console.error('❌ Error saving product:', error);
      setProductError(error.message || 'Failed to save product. Please try again.');
    } finally {
      setProductLoading(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!product?.id) return;
    try {
      await productAPI.deleteProduct(product.id);
      setProductSuccessMessage('Product deactivated.');
      await fetchProductsForOrg({ silent: true });
    } catch (error) {
      console.error('❌ Error deleting product:', error);
      setProductError(error.message || 'Failed to update product status.');
    }
  };

  const renderProductManagement = () => (
    <Box>
      {productSuccessMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setProductSuccessMessage('')}>
          {productSuccessMessage}
        </Alert>
      )}
      {productError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProductError('')}>
          {productError}
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Product Catalog
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => fetchProductsForOrg()}
                disabled={productLoading}
                sx={{ textTransform: 'none' }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetProductFormState();
                  setProductDialogOpen(true);
                }}
                sx={{ textTransform: 'none', bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
              >
                Add New Product
              </Button>
            </Box>
          </Box>

          {productLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : products.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <InventoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="subtitle1" color="text.secondary">
                No products available yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click “Add New Product” to create your first entry.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product Name</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Volume / Size</TableCell>
                    <TableCell align="right">Sale Price</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Image</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {product.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{product.sku}</TableCell>
                      <TableCell>{product.volume || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        ${Number(product.basePrice || 0).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {product.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: 120 }}>
                        {Array.isArray(product.images) && product.images.length > 0 ? (
                          <Box
                            component="img"
                            src={product.images[0]}
                            alt={product.name}
                            sx={{
                              width: 96,
                              height: 64,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid rgba(148, 163, 184, 0.25)'
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No image
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Edit product">
                            <IconButton
                              size="small"
                              onClick={() => openProductDialog(product)}
                              sx={{ color: '#2563eb' }}
                            >
                              <EditIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deactivate product">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteProduct(product)}
                              sx={{ color: '#dc2626' }}
                            >
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={productDialogOpen}
        onClose={() => {
          setProductDialogOpen(false);
          resetProductFormState();
        }}
        maxWidth="sm"
        fullWidth
      >
        <Box component="form" onSubmit={handleProductSave}>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {productForm.id ? 'Edit Product' : 'Add New Product'}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Product Name"
                value={productForm.name}
                onChange={handleProductFieldChange('name')}
                required
                fullWidth
              />
              <TextField
                label="Product Code / SKU"
                value={productForm.sku}
                onChange={handleProductFieldChange('sku')}
                required
                fullWidth
                helperText="Visible in batch creation and ordering (e.g. EQC-001)"
              />
              <TextField
                label="Description"
                value={productForm.description}
                onChange={handleProductFieldChange('description')}
                multiline
                minRows={3}
                fullWidth
                placeholder="Short summary for clinical teams"
              />
              <TextField
                label="Volume / Size"
                value={productForm.volume}
                onChange={handleProductFieldChange('volume')}
                fullWidth
                placeholder="Optional (e.g. 5 mL vial)"
              />
              <TextField
                label="Sale Price"
                value={productForm.basePrice}
                onChange={handleProductFieldChange('basePrice')}
                type="number"
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
              <Box>
                <Button variant="outlined" component="label" sx={{ textTransform: 'none' }}>
                  {productForm.imageFile ? 'Change Image' : 'Upload Image'}
                  <input hidden accept="image/*" type="file" onChange={handleProductImageChange} />
                </Button>
                {productForm.imagePreview && productForm.imagePreview !== '' && (
                  <Box sx={{ mt: 2 }}>
                    <Box
                      component="img"
                      src={productForm.imagePreview}
                      alt="Product preview"
                      sx={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid rgba(148, 163, 184, 0.25)'
                      }}
                      onError={(e) => {
                        // If image fails to load, clear it
                        console.warn('Image failed to load, clearing preview');
                        handleRemoveProductImage();
                      }}
                    />
                    <Button
                      size="small"
                      color="secondary"
                      onClick={handleRemoveProductImage}
                      sx={{ mt: 1, textTransform: 'none' }}
                    >
                      Remove Image
                    </Button>
                  </Box>
                )}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => {
                setProductDialogOpen(false);
                resetProductFormState();
              }}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={productLoading}
              sx={{ textTransform: 'none', bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
            >
              {productLoading ? 'Saving...' : productForm.id ? 'Save Changes' : 'Create Product'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );

  const ensureBatchSkus = async (batch) => {
    const batchId = batch?.id;
    if (!batchId) return [];

    if (batchSkusMap[batchId]) {
      return batchSkusMap[batchId];
    }

    setBatchSkusLoading((prev) => ({ ...prev, [batchId]: true }));
    try {
      const token = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
        'X-Client-Version': '2.0.0',
        'X-HIPAA-Compliant': 'true'
      };

      const response = await fetch(`${API_BASE_URL}/inventory-skus?inventoryId=${batchId}`, {
        credentials: 'include',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch SKUs for batch ${batchId}`);
      }

      const result = await response.json();
      const skus = Array.isArray(result?.data) ? result.data : [];
      setBatchSkusMap((prev) => ({ ...prev, [batchId]: skus }));
      return skus;
    } catch (error) {
      console.error('Error fetching SKUs for batch:', batchId, error);
      setBatchErrorMessage('Failed to load tissue IDs. Please try again.');
      return [];
    } finally {
      setBatchSkusLoading((prev) => ({ ...prev, [batchId]: false }));
    }
  };

  const getBatchSkus = (batchId) => batchSkusMap[batchId] || [];

  const getPaginatedBatchSKUs = (batchId) => {
    const skus = getBatchSkus(batchId);
    const currentPage = batchSkuPages[batchId] || 1;
    const start = (currentPage - 1) * itemsPerPage;
    return skus.slice(start, start + itemsPerPage);
  };

  const handleBatchSkuPageChange = (batchId, page) => {
    setBatchSkuPages((prev) => ({ ...prev, [batchId]: page }));
  };

  const handleToggleBatchExpanded = async (batch) => {
    if (!batch?.id) return;
    const isExpanding = expandedBatchId !== batch.id;
    setExpandedBatchId(isExpanding ? batch.id : null);
    if (isExpanding) {
      await ensureBatchSkus(batch);
    }
  };

  const handleViewBatchDetails = async (batch) => {
    if (!batch) return;
    await ensureBatchSkus(batch);
    setSelectedBatchDetails({
      ...batch,
      skus: getBatchSkus(batch.id)
    });
    setSelectedSkuDetails(null);
    setDetailDialogOpen(true);
  };

  const handleViewSkuDetails = async (batch, sku) => {
    if (!batch || !sku) return;
    if (!batchSkusMap[batch.id]) {
      await ensureBatchSkus(batch);
    }
    setSelectedSkuDetails({
      ...sku,
      batch
    });
    setSelectedBatchDetails(null);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedBatchDetails(null);
    setSelectedSkuDetails(null);
  };

  const exportBatchConfirmationCSV = async () => {
    if (!batches.length) {
      setBatchErrorMessage('No batches available to export.');
      return;
    }

    try {
      setBatchErrorMessage('');
      setSuccessMessage('');
      setExportingBatches(true);

      const enrichedBatches = [];
      for (const batch of batches) {
        const skus = await ensureBatchSkus(batch);
        enrichedBatches.push({ batch, skus });
      }

      const headers = [
        'Batch ID',
        'Product Name',
        'Product SKU',
        'Donor ID (Lot Number)',
        'Expected Quantity',
        'Quantity Received',
        'Quantity Available',
        'Quantity Reserved',
        'Quantity Allocated',
        'Status',
        'QC Status',
        'Expiration Date',
        'Manufacturing Date',
        'Created Date',
        'Received Date',
        'Warehouse',
        'Zone',
        'Shelf',
        'Bin',
        'Location',
        'Tissue IDs',
        'Tissue ID Count'
      ];

      const rows = enrichedBatches.map(({ batch, skus }) => {
        const location = [batch.warehouse, batch.zone, batch.shelf, batch.bin]
          .filter(Boolean)
          .join('-') || '-';
        const tissueIds = skus
          .map((sku) => sku.tissueId || sku.sku || sku.id)
          .filter(Boolean);

        const values = [
          batch.batchId || '',
          batch.productName || '',
          batch.product?.sku || batch.productSku || '',
          batch.lotNumber || '',
          batch.expectedQuantity ?? batch.quantityReceived ?? 0,
          batch.quantityReceived ?? 0,
          batch.quantityAvailable ?? 0,
          batch.quantityReserved ?? 0,
          batch.quantityAllocated ?? 0,
          batch.status || '',
          batch.qcStatus || '',
          batch.expirationDate ? new Date(batch.expirationDate).toLocaleDateString() : '',
          batch.manufacturingDate ? new Date(batch.manufacturingDate).toLocaleDateString() : '',
          batch.createdAt ? new Date(batch.createdAt).toLocaleDateString() : '',
          batch.receivedAt ? new Date(batch.receivedAt).toLocaleDateString() : '',
          batch.warehouse || '',
          batch.zone || '',
          batch.shelf || '',
          batch.bin || '',
          location,
          tissueIds.join('; '),
          tissueIds.length
        ];

        return values.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',');
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = `batch-confirmation-export-${new Date().toISOString().slice(0, 10)}.csv`;
      downloadLink.click();
      URL.revokeObjectURL(url);

      setSuccessMessage('Batch confirmation data exported successfully.');
    } catch (error) {
      console.error('Error exporting batch confirmation CSV:', error);
      setBatchErrorMessage('Failed to export batch confirmation data. Please try again.');
    } finally {
      setExportingBatches(false);
    }
  };

  // Filter batches
  const getFilteredBatches = () => {
    return batches.filter(batch => {
      const matchesSearch = !searchTerm || 
        batch.batchId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        batch.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        batch.lotNumber.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesBatch = !filterBatch || batch.batchId.includes(filterBatch);
      const matchesLot = !filterLot || batch.lotNumber.includes(filterLot);
      const matchesStatus = filterStatus === 'ALL' || batch.qcStatus === filterStatus;
      
      let matchesExpiration = true;
      if (filterExpiration) {
        const expirationMonth = new Date(batch.expirationDate).toISOString().substring(0, 7);
        matchesExpiration = expirationMonth === filterExpiration;
      }

      // Filter by active tab
      let matchesTab = true;
      if (orgKey === 'epl') {
        switch (activeTab) {
          case 0:
            matchesTab = batch.qcStatus === 'PENDING';
            break;
          case 1:
            matchesTab = batch.qcStatus === 'IN_PROGRESS';
            break;
          case 2:
            matchesTab = batch.qcStatus === 'COMPLETED';
            break;
          default:
            matchesTab = true;
        }
      }

      return matchesSearch && matchesBatch && matchesLot && matchesStatus && matchesExpiration && matchesTab;
    });
  };

  const getStatusChip = (status) => {
    const normalized = normalizeBatchStatus(status);
    const statusConfig = {
      COMPLETED: { color: 'success', icon: <ApprovedIcon />, label: 'Completed' },
      IN_PROGRESS: { color: 'info', icon: <ScheduleIcon />, label: 'In Progress' },
      PENDING: { color: 'warning', icon: <PendingIcon />, label: 'Pending' },
      FAILED: { color: 'error', icon: <WarningIcon />, label: 'Failed QC' }
    };
    const config = statusConfig[normalized] || statusConfig.PENDING;
    return (
      <Chip 
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const getExpirationStatus = (expirationDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    const expiry = new Date(expirationDate);
    expiry.setHours(0, 0, 0, 0); // Normalize to start of day
    const daysUntilExpiry = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { color: 'error', text: 'Expired', days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 90) {
      return { color: 'warning', text: 'Expiring Soon', days: daysUntilExpiry };
    } else {
      return { color: 'success', text: 'Good', days: daysUntilExpiry };
    }
  };

  const uniqueBatches = [...new Set(batches.map(b => b.batchId))];
  const uniqueLots = [...new Set(batches.map(b => b.lotNumber))];
  const uniqueMonths = [...new Set(batches.map(b => new Date(b.expirationDate).toISOString().substring(0, 7)))].sort();

  return (
    <Box>
      <Breadcrumbs />
      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}
      {batchErrorMessage && (
        <Alert severity="error" onClose={() => setBatchErrorMessage('')} sx={{ mb: 3 }}>
          {batchErrorMessage}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{
        mb: 4,
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 2,
        boxShadow: '0 4px 12px rgba(3, 149, 130, 0.3)'
      }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
            Receive Inventory - {currentCompany.toUpperCase()}
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            {orgKey === 'epl' ? 'Create batches and manage inventory directly' : 'Process incoming purchase orders and add inventory to the system'}
          </Typography>
        </Box>
        {orgKey === 'epl' && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={exportingBatches ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={exportBatchConfirmationCSV}
              disabled={exportingBatches || !batches.length}
              sx={{
                bgcolor: 'white',
                color: '#039582',
                px: 3,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(255, 255, 255, 0.4)'
                },
                '&:disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.6)',
                  color: 'rgba(3, 149, 130, 0.6)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {exportingBatches ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateBatchOpen(true)}
              sx={{
                bgcolor: 'white',
                color: '#039582',
                px: 3,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(255, 255, 255, 0.4)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Create Batch
            </Button>
          </Box>
        )}
      </Box>

      {/* Tabs - Following OrderProcessing pattern */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(Number(newValue))}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 48,
              },
              '& .Mui-selected': {
                color: '#039582',
              },
              '& .MuiTabs-indicator': {
                bgcolor: '#039582',
              },
            }}
          >
            <Tab value={0} label={orgKey === 'epl' ? `Active Batches ${batches.filter(b => b.qcStatus === 'PENDING').length}` : `Pending Receipt ${purchaseOrders.filter(po => ['PENDING', 'ORDERED'].includes(po.status)).length}`} />
            <Tab value={1} label={orgKey === 'epl' ? `In Progress ${batches.filter(b => b.qcStatus === 'PENDING').length}` : `In Progress ${purchaseOrders.filter(po => po.status === 'RECEIVING').length}`} />
            <Tab value={2} label={orgKey === 'epl' ? `Completed ${batches.filter(b => b.qcStatus === 'PASSED' || b.qcStatus === 'COMPLETED').length}` : `Completed ${purchaseOrders.filter(po => po.status === 'RECEIVED').length}`} />
            <Tab value={3} label="Product Management" />
          </Tabs>
        </Box>

        <CardContent>
          {orgKey !== 'epl' ? (
            activeTab === 3 ? (
              renderProductManagement()
            ) : (
              // BARE - Purchase Order Workflow
              loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : filteredPurchaseOrders.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <InventoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No purchase orders in this status
                  </Typography>
                </Box>
              ) : isMobile ? (
                renderMobilePOs()
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>PO Number</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Items</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Expected</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPurchaseOrders.map((po) => (
                        <TableRow key={po.id} hover>
                          <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                            {po.poNumber}
                          </TableCell>
                          <TableCell>{po.vendor}</TableCell>
                          <TableCell>
                            {po.items.map((item, idx) => (
                              <Typography key={idx} variant="body2">
                                {typeof item.product === 'object' && item.product !== null 
                                  ? item.product.name || item.product.sku || 'Unknown Product'
                                  : item.product || 'Unknown Product'} × {item.quantity - item.quantityReceived}
                              </Typography>
                            ))}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={po.status}
                              color={getStatusColor(po.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(po.expectedDelivery).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            ${(Number(po.totalAmount) || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            {activeTab === 0 && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<StartIcon />}
                                onClick={() => handleStartReceiving(po)}
                                sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                              >
                                {po.status === 'ORDERED' ? 'Receive' : 'Continue'}
                              </Button>
                            )}
                            {activeTab === 1 && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ViewIcon />}
                                onClick={() => handleStartReceiving(po)}
                                sx={{ borderColor: '#3b82f6', color: '#3b82f6' }}
                              >
                                Continue
                              </Button>
                            )}
                            {activeTab === 2 && (
                              <Stack direction="row" spacing={1} justifyContent="center">
                                <IconButton
                                  size="small"
                                  onClick={() => handleStartReceiving(po)}
                                  sx={{ color: '#10b981' }}
                                  title="View Details"
                                >
                                  <ViewIcon />
                                </IconButton>
                                {isSuperAdmin && (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditRecord(po)}
                                      sx={{ color: '#2563eb' }}
                                      title="Edit Record"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteRecord(po)}
                                      sx={{ color: '#dc2626' }}
                                      title="Delete Record"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </>
                                )}
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )
          ) : activeTab === 3 ? (
            renderProductManagement()
          ) : (
            // EPL - Batch Workflow
            getFilteredBatches().length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ScienceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No batches in this status
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {activeTab === 0 && 'Create a batch to get started'}
                </Typography>
              </Box>
            ) : (
              isMobile ? (
                <Stack spacing={1.5} sx={{ p: 2 }}>
                  {getFilteredBatches().map((batch) => (
                    <Paper key={batch.id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {batch.createdAt ? new Date(batch.createdAt).toLocaleDateString() : '-'}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                            {batch.batchId}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {batch.productName}
                          </Typography>
                        </Box>
                        <Chip
                          label={batch.status}
                          color={batch.status === 'COMPLETED' ? 'success' : batch.status === 'RECEIVING' ? 'info' : 'warning'}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Donor ID: {batch.lotNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Expected: {batch.quantityReceived || batch.expectedQuantity}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Expires: {new Date(batch.expirationDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Chip 
                          label={`${batch.scannedQuantity || 0} / ${batch.expectedQuantity} scanned`}
                          size="small"
                          color={(batch.scannedQuantity || 0) >= batch.expectedQuantity ? 'success' : 'warning'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {activeTab === 0 && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<StartIcon />}
                              onClick={() => handleStartReceiving(batch)}
                              sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                            >
                              Confirm SKUs
                            </Button>
                            {(batch.qcStatus === 'PENDING' || batch.qcStatus === null) && (
                              <Tooltip title="Delete batch (only available for active batches)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteRecord(batch)}
                                  sx={{ color: '#dc2626' }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                        {activeTab === 1 && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ViewIcon />}
                              onClick={() => handleStartReceiving(batch)}
                              sx={{ borderColor: '#3b82f6', color: '#3b82f6' }}
                            >
                              Continue
                            </Button>
                            {(batch.qcStatus === 'PENDING' || batch.qcStatus === null) && (
                              <Tooltip title="Delete batch (only available for active batches)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteRecord(batch)}
                                  sx={{ color: '#dc2626' }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                        {activeTab === 2 && (
                          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                            <IconButton
                              size="small"
                              onClick={() => handleStartReceiving(batch)}
                              sx={{ color: '#2563eb' }}
                              title="Manage Batch"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleViewCertificate(batch)}
                              sx={{ color: '#10b981' }}
                              title="View Quality Certificate"
                            >
                              <CertificateIcon />
                            </IconButton>
                          </Stack>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 1000 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Batch</TableCell>
                        <TableCell>Donor ID</TableCell>
                        <TableCell>Expected Qty</TableCell>
                        <TableCell>Scanned</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Expiration</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getFilteredBatches().map((batch) => {
                        const expirationStatus = getExpirationStatus(batch.expirationDate);

                        return (
                          <TableRow key={batch.id} hover>
                              <TableCell sx={{ minWidth: 200 }}>
                                <Stack spacing={0.5}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                    {batch.batchId}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {batch.productName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {batch.createdAt ? new Date(batch.createdAt).toLocaleDateString() : '-'}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                                {batch.lotNumber}
                              </TableCell>
                              <TableCell>
                                {batch.quantityReceived || batch.expectedQuantity}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={`${batch.scannedQuantity || 0} / ${batch.expectedQuantity}`}
                                  size="small"
                                  color={(batch.scannedQuantity || 0) >= batch.expectedQuantity ? 'success' : 'warning'}
                                />
                              </TableCell>
                              <TableCell>
                                {getStatusChip(batch.status)}
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.5}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {batch.expirationDate ? new Date(batch.expirationDate).toLocaleDateString() : '-'}
                                  </Typography>
                                  {batch.expirationDate && (
                                    <Chip
                                      label={`${expirationStatus.days} days`}
                                      size="small"
                                      color={expirationStatus.color}
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {[batch.warehouse, batch.zone, batch.shelf, batch.bin].filter(Boolean).join('-') || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                                  {activeTab === 0 && (
                                    <>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<StartIcon />}
                                        onClick={() => handleStartReceiving(batch)}
                                        sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                                      >
                                        Confirm
                                      </Button>
                                      {(batch.qcStatus === 'PENDING' || batch.qcStatus === null) && (
                                        <Tooltip title="Delete batch (only available for active batches)">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteRecord(batch)}
                                            sx={{ color: '#dc2626' }}
                                          >
                                            <DeleteIcon fontSize="inherit" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </>
                                  )}
                                  {activeTab === 1 && (
                                    <>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<ViewIcon />}
                                        onClick={() => handleStartReceiving(batch)}
                                        sx={{ borderColor: '#3b82f6', color: '#3b82f6' }}
                                      >
                                        Continue
                                      </Button>
                                      {(batch.qcStatus === 'PENDING' || batch.qcStatus === null) && (
                                        <Tooltip title="Delete batch (only available for active batches)">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteRecord(batch)}
                                            sx={{ color: '#dc2626' }}
                                          >
                                            <DeleteIcon fontSize="inherit" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </>
                                  )}
                                  {activeTab === 2 && (
                                    <>
                                      <Tooltip title="Manage batch">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleStartReceiving(batch)}
                                          sx={{ color: '#2563eb' }}
                                        >
                                          <EditIcon fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="View quality certificate">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleViewCertificate(batch)}
                                          sx={{ color: '#10b981' }}
                                        >
                                          <CertificateIcon fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                      {isSuperAdmin && (
                                        <Tooltip title="Delete record">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteRecord(batch)}
                                            sx={{ color: '#dc2626' }}
                                          >
                                            <DeleteIcon fontSize="inherit" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )
          )}
        </CardContent>
      </Card>

      {/* Batch / SKU Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedBatchDetails ? 'Batch Details' : selectedSkuDetails ? 'SKU Details' : 'Details'}
          {selectedBatchDetails && getStatusChip(selectedBatchDetails.status)}
          {selectedSkuDetails && (
            <Chip
              label={selectedSkuDetails.status || 'AVAILABLE'}
              size="small"
              color="primary"
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedBatchDetails ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Batch Information
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Batch ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                        {selectedBatchDetails.batchId}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Product
                      </Typography>
                      <Typography variant="body1">
                        {selectedBatchDetails.productName}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Donor ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                        {selectedBatchDetails.lotNumber || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Inventory Quantities
                  </Typography>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Expected</Typography>
                      <Chip label={selectedBatchDetails.expectedQuantity || 0} size="small" />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Received</Typography>
                      <Chip label={selectedBatchDetails.quantityReceived || 0} size="small" color="primary" />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Available</Typography>
                      <Chip label={selectedBatchDetails.quantityAvailable || 0} size="small" color="success" />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Reserved</Typography>
                      <Chip label={selectedBatchDetails.quantityReserved || 0} size="small" color="warning" />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Allocated</Typography>
                      <Chip label={selectedBatchDetails.quantityAllocated || 0} size="small" color="info" />
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" /> Dates
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Created
                      </Typography>
                      <Typography variant="body1">
                        {selectedBatchDetails.createdAt ? new Date(selectedBatchDetails.createdAt).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Expiration
                      </Typography>
                      <Typography variant="body1">
                        {selectedBatchDetails.expirationDate ? new Date(selectedBatchDetails.expirationDate).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" /> Location
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Warehouse
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {selectedBatchDetails.warehouse || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Zone / Shelf / Bin
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {[selectedBatchDetails.zone, selectedBatchDetails.shelf, selectedBatchDetails.bin].filter(Boolean).join('-') || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          ) : selectedSkuDetails ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    SKU Information
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Tissue ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                        {selectedSkuDetails.tissueId || selectedSkuDetails.sku || selectedSkuDetails.id}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Product
                      </Typography>
                      <Typography variant="body1">
                        {selectedSkuDetails.batch?.productName || selectedSkuDetails.productName || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Batch ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                        {selectedSkuDetails.batch?.batchId || selectedSkuDetails.batchId || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarehouseIcon fontSize="small" /> Location & Dates
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Location
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {[selectedSkuDetails.warehouse, selectedSkuDetails.zone, selectedSkuDetails.shelf, selectedSkuDetails.bin]
                          .filter(Boolean)
                          .join('-') || selectedSkuDetails.location || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Expiration
                      </Typography>
                      <Typography variant="body1">
                        {selectedSkuDetails.expirationDate ? new Date(selectedSkuDetails.expirationDate).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Received
                      </Typography>
                      <Typography variant="body1">
                        {selectedSkuDetails.receivedDate ? new Date(selectedSkuDetails.receivedDate).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Order Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {selectedSkuDetails.orderNumber || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDetailDialog} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receiving Workflow Dialog - Following OrderProcessing pattern */}
      <Dialog 
        open={processDialogOpen} 
        onClose={() => setProcessDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {selectedPO?.batchId ? `Receive Batch: ${selectedPO?.batchId}` : `Receive Purchase Order: ${selectedPO?.poNumber}`}
            </Typography>
            <Chip 
              label={selectedPO?.status} 
              color={getStatusColor(selectedPO?.status)}
              size="small"
            />
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3}>
            {/* Left: Simple Info Card */}
            <Grid item xs={12} md={5}>
              {selectedPO && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {selectedPO?.batchId || selectedPO?.poNumber}
                    </Typography>
                    <Chip 
                      label={selectedPO?.organization || 'Unknown'} 
                      color={selectedPO?.organization === 'EPL' ? 'primary' : 'secondary'} 
                      size="small"
                      sx={{ fontWeight: 600, mb: 2 }}
                    />
                    {selectedPO?.batchId && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Lot: {selectedPO?.lotNumber}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {selectedPO?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0} items to receive
                    </Typography>
                    {selectedPO?.batchId && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Expires: {new Date(selectedPO?.expirationDate).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}
            </Grid>

            {/* Right: Workflow Steps */}
            <Grid item xs={12} md={7}>
              <Stepper activeStep={currentStep} orientation="vertical">

                {/* Step 0: Assignment & Start / Batch Confirmation */}
                <Step>
                  <StepLabel>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {selectedPO?.batchId ? 'Batch Confirmation' : 'Assignment & Start Receiving'}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    {selectedPO?.batchId ? (
                      // EPL Batch Confirmation
                      <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Review the generated tissue IDs for this batch. All tissue IDs are pre-created and ready for quality control.
                        </Alert>
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                            Generated Tissue IDs ({receivingItems.length}):
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {receivingItems.map((item, index) => (
                              <Chip
                                key={item.id || index}
                                label={item.tissueId || item.sku}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                            ))}
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Batch: {selectedPO.batchId} | Lot: {selectedPO.lotNumber} | 
                            Expires: {new Date(selectedPO.expirationDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                        
                        <Button
                          variant="contained"
                          onClick={() => {
                            setCurrentStep(1); // Go directly to Quality Control (was Step 2)
                            const updated = { 
                              ...selectedPO, 
                              receivingStartedAt: new Date().toISOString(), 
                              status: 'RECEIVING' 
                            };
                            setSelectedPO(updated);
                          }}
                          sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                        >
                          Confirm & Proceed to QC
                        </Button>
                      </>
                    ) : (
                      // Regular Purchase Order
                      <>
                        <Alert severity="success" sx={{ mb: 2 }}>
                          Purchase order automatically assigned to you for receiving
                        </Alert>

                        <Box>
                          <Typography variant="body2" color="text.secondary">Assigned At:</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                            {formatDateTime(selectedPO?.assignedAt || new Date().toISOString())}
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={() => {
                              setCurrentStep(1);
                              const updated = { 
                                ...selectedPO, 
                                receivingStartedAt: new Date().toISOString(), 
                                status: 'RECEIVING' 
                              };
                              setSelectedPO(updated);
                            }}
                            sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                          >
                            Start Receiving Items
                          </Button>
                        </Box>
                      </>
                    )}
                  </StepContent>
                </Step>

                {/* Step 1: Quality Control & Certificate Upload */}
                <Step>
                  <StepLabel>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Quality Control & Certificate Upload
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    {/* Quality Certificate Upload - REQUIRED for completion */}
                    <Paper sx={{ p: 3, mb: 3, bgcolor: '#fef3c7', border: '2px solid #f59e0b' }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#92400e', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CertificateIcon /> Quality Certificate Upload (Required)
                      </Typography>
                      
                      {!qualityCertificate ? (
                        <Box>
                          <Typography variant="body2" color="#92400e" sx={{ mb: 2 }}>
                            Upload the quality certificate from the vendor to complete the receiving process.
                          </Typography>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                            id="certificate-upload-final"
                          />
                          <label htmlFor="certificate-upload-final">
                            <Button
                              variant="contained"
                              component="span"
                              startIcon={certificateUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                              disabled={certificateUploading}
                              size="large"
                              sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' }, px: 4 }}
                            >
                              {certificateUploading ? 'Uploading...' : 'Upload Quality Certificate'}
                            </Button>
                          </label>
                          {certificateError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                              {certificateError}
                            </Alert>
                          )}
                        </Box>
                      ) : (
                        <Box>
                          <Alert severity="success" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              ✅ Quality certificate uploaded: <strong>{qualityCertificate.name}</strong>
                            </Typography>
                            <Typography variant="body2">
                              Uploaded: {new Date(qualityCertificate.uploadedAt).toLocaleString()} •
                              Size: {(qualityCertificate.size / 1024).toFixed(1)}KB
                            </Typography>
                          </Alert>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setQualityCertificate(null)}
                          >
                            Replace Certificate
                          </Button>
                        </Box>
                      )}
                    </Paper>

                    <Grid container spacing={3}>
                      {/* Receiving Details */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                            Receiving Details
                          </Typography>
                          {/* Invoice number removed per business rule: invoices appear only at sale time */}
                          {/* Tracking number removed from receiving */}
                          {/* Receiving notes removed per business rule */}
                        </Paper>
                      </Grid>

                      {/* Completion Checklist */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                            Completion Checklist
                          </Typography>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckIcon color={receivingItems.every(item => 
                                item.receivedQuantity >= (selectedPO?.items?.find(poItem => 
                                  poItem.productId === item.productId
                                )?.quantity || 0)
                              ) ? 'success' : 'disabled'} />
                              <Typography variant="body2">
                                All items received and scanned
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckIcon color={qualityCertificate ? 'success' : 'disabled'} />
                              <Typography variant="body2" sx={{ fontWeight: qualityCertificate ? 400 : 600, color: qualityCertificate ? 'inherit' : 'error.main' }}>
                                Quality certificate uploaded (Required)
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {/* Removed invoice indicator */}
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleCompleteReceiving}
                        disabled={!qualityCertificate}
                        sx={{ 
                          bgcolor: '#10b981', 
                          '&:hover': { bgcolor: '#059669' },
                          px: 4,
                          py: 1.5,
                          fontSize: '1.1rem'
                        }}
                      >
                        Complete Receiving Process
                      </Button>
                      {!qualityCertificate && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            Quality certificate upload is required to complete the receiving process
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  </StepContent>
                </Step>
              </Stepper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setProcessDialogOpen(false)}>
            Exit (Progress Saved)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Batch Dialog - EPL Only */}
      <Dialog 
        open={createBatchOpen} 
        onClose={() => setCreateBatchOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: alpha('#5c725c', 0.05), borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ScienceIcon sx={{ color: '#5c725c' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Create New Batch
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Product</InputLabel>
                <Select
                  value={batchForm.productId}
                  onChange={(e) => setBatchForm({ ...batchForm, productId: e.target.value })}
                  label="Product"
                >
                  {/* Load products dynamically from backend */}
                  {productOptions.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name} ({product.sku})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {orgUpper === 'EPL' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Donor ID *</InputLabel>
                  <Select
                    value={batchForm.donorId}
                    onChange={(e) => {
                      const selectedDonor = donorOptions.find(d => d.id === e.target.value);
                      setBatchForm({ 
                        ...batchForm, 
                        donorId: e.target.value,
                        lotNumber: selectedDonor ? selectedDonor.donorId : ''
                      });
                    }}
                    label="Donor ID *"
                    disabled={donorsLoading}
                    required
                  >
                    {donorOptions.map((donor) => (
                      <MenuItem key={donor.id} value={donor.id}>
                        {donor.donorId} - {donor.mareName}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select the donor this batch was processed from
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Starting Lot Number"
                value={batchForm.startingSku}
                onChange={(e) => setBatchForm({ ...batchForm, startingSku: e.target.value })}
                placeholder="L-05"
                helperText="First SKU in sequence; system will auto-generate sequential SKUs"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Number of SKUs"
                value={batchForm.quantity}
                onChange={(e) => setBatchForm({ ...batchForm, quantity: e.target.value })}
                placeholder="100"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InventoryIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Expiration Date"
                value={batchForm.expirationDate}
                onChange={(e) => setBatchForm({ ...batchForm, expirationDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: new Date().toISOString().split('T')[0]
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ScheduleIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Manufacturing Date"
                value={batchForm.manufacturingDate}
                onChange={(e) => setBatchForm({ ...batchForm, manufacturingDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Cost Per Unit"
                value={batchForm.costPerUnit}
                onChange={(e) => setBatchForm({ ...batchForm, costPerUnit: e.target.value })}
                placeholder="0.00"
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  )
                }}
                helperText="Cost per unit for this batch"
              />
            </Grid>


            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 1 }}>
                Storage location and quality control details will be added during the receiving process.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
          <Button 
            onClick={() => setCreateBatchOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateBatch}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
            sx={{
              bgcolor: '#5c725c',
              textTransform: 'none',
              px: 4,
              '&:hover': { bgcolor: '#4a5d4a' }
            }}
          >
            {loading ? 'Creating...' : 'Create Batch'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Inventory Record Dialog - Super Admin Only */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Edit Inventory Record
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Lot Number"
                value={editForm.lotNumber}
                disabled
                helperText="Lot number cannot be changed after creation"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Expiration Date"
                value={editForm.expirationDate}
                InputLabelProps={{ shrink: true }}
                disabled
                helperText="Expiration date cannot be changed after creation"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Quantity Received"
                value={editForm.quantityReceived}
                disabled
                helperText="Quantity received cannot be changed after creation"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Quantity Available"
                value={editForm.quantityAvailable}
                onChange={(e) => setEditForm({ ...editForm, quantityAvailable: e.target.value })}
                disabled={!isSuperAdmin}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Warehouse"
                value={editForm.warehouse}
                onChange={(e) => setEditForm({ ...editForm, warehouse: e.target.value })}
                disabled={!isSuperAdmin}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Zone"
                value={editForm.zone}
                onChange={(e) => setEditForm({ ...editForm, zone: e.target.value })}
                disabled={!isSuperAdmin}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Shelf"
                value={editForm.shelf}
                onChange={(e) => setEditForm({ ...editForm, shelf: e.target.value })}
                disabled={!isSuperAdmin}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bin"
                value={editForm.bin}
                onChange={(e) => setEditForm({ ...editForm, bin: e.target.value })}
                disabled={!isSuperAdmin}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>QC Status</InputLabel>
                <Select
                  value={editForm.qcStatus}
                  onChange={(e) => setEditForm({ ...editForm, qcStatus: e.target.value })}
                  label="QC Status"
                  disabled={!isSuperAdmin}
                >
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="PASSED">Passed</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="FAILED">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="QC Notes"
                value={editForm.qcNotes}
                onChange={(e) => setEditForm({ ...editForm, qcNotes: e.target.value })}
                disabled={!isSuperAdmin}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={editLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={editLoading || !isSuperAdmin}
            sx={{ textTransform: 'none', bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
          >
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#dc2626' }}>
          {orgKey === 'epl' ? 'Delete Batch' : 'Delete Inventory Record'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Are you sure you want to delete this {orgKey === 'epl' ? 'batch' : 'inventory record'}?
            </Typography>
            <Typography variant="body2">
              This action cannot be undone. The record for{' '}
              <strong>{selectedRecord?.batchId || selectedRecord?.poNumber}</strong> will be permanently deleted.
            </Typography>
            {orgKey === 'epl' && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>This will also delete:</strong>
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                  <li>All SKUs associated with this batch</li>
                  <li>All inventory movement records</li>
                  <li>The batch record itself</li>
                </ul>
              </Typography>
            )}
          </Alert>
          {selectedRecord && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Product:</strong> {selectedRecord.productName || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Lot Number:</strong> {selectedRecord.lotNumber || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Quantity:</strong> {selectedRecord.quantityReceived || selectedRecord.expectedQuantity || 0}
              </Typography>
              {orgKey === 'epl' && selectedRecord.qcStatus && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Status:</strong> {selectedRecord.qcStatus}
                  {selectedRecord.qcStatus !== 'PENDING' && selectedRecord.qcStatus !== null && (
                    <Chip label="Cannot delete completed batches" size="small" color="error" sx={{ ml: 1 }} />
                  )}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleteLoading || (orgKey !== 'epl' && !isSuperAdmin)}
            sx={{
              textTransform: 'none',
              bgcolor: '#dc2626',
              '&:hover': { bgcolor: '#b91c1c' }
            }}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Record'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ReceiveInventoryWorkflow;